import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import Contact from "@/pages/Contact";
import DetailedQuestionnaire from "@/pages/DetailedQuestionnaire";
import Directories from "@/pages/Directories";
import SampleWork from "@/pages/SampleWork";
import HireRep from "@/pages/HireRep";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline"; // eslint-disable-line no-unused-vars -- kept for /pipeline redirect
import Contacts from "@/pages/Contacts";
import CrmCompanies from "@/pages/CrmCompanies";
import Products from "@/pages/Products";
import Calls from "@/pages/Calls";
import SalesReps from "@/pages/SalesReps";
import CompanyProfile from "@/pages/CompanyProfile";
import Inquiries from "@/pages/Inquiries";
import Reports from "@/pages/Reports";
import Files from "@/pages/Files";
import Settings from "@/pages/Settings";
import PublicCompany from "@/pages/PublicCompany";
import SuperAdmin from "@/pages/SuperAdmin";
import AdminPartners from "@/pages/AdminPartners";
import Partners from "@/pages/Partners";
import OwnerReports from "@/pages/OwnerReports";
import OwnerCallsEmails from "@/pages/OwnerCallsEmails";
import Chat from "@/pages/Chat";
import WorkReport from "@/pages/WorkReport";
import AILeadFinder from "@/pages/AILeadFinder";
import AgencyWorkbench from "@/pages/AgencyWorkbench";
import EmailAgent from "@/pages/EmailAgent";
import { TwilioVoiceProvider } from "@/context/TwilioVoiceContext";
import Softphone from "@/components/Softphone";
import PostCallModal from "@/components/PostCallModal";

function AppRoot() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/directories" element={<Directories />} />
        <Route path="/sample-work" element={<Navigate to="/contact" replace />} />
        <Route path="/hire-rep" element={<HireRep />} />
        <Route path="/questionnaire" element={<DetailedQuestionnaire />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/c/:slug" element={<PublicCompany />} />
        <Route path="/partners" element={<Partners />} />

        {/* App (protected) */}
        <Route path="/app" element={<ProtectedRoute><TwilioVoiceProvider><Layout /><Softphone /><PostCallModal /></TwilioVoiceProvider></ProtectedRoute>}>
          <Route index element={<HomeRoute />} />
          <Route path="pipeline" element={<Navigate to="/app/work-report" replace />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="crm-companies" element={<CrmCompanies />} />
          <Route path="products" element={<ProtectedRoute roles={["company_owner", "super_admin"]}><Products /></ProtectedRoute>} />
          <Route path="calls" element={<Calls />} />
          <Route path="sales-reps" element={<ProtectedRoute roles={["company_owner"]}><SalesReps /></ProtectedRoute>} />
          <Route path="profile" element={<ProtectedRoute roles={["company_owner"]}><CompanyProfile /></ProtectedRoute>} />
          <Route path="inquiries" element={<ProtectedRoute roles={["company_owner"]}><Inquiries /></ProtectedRoute>} />
          <Route path="reports" element={<ProtectedRoute roles={["company_owner"]}><Reports /></ProtectedRoute>} />
          <Route path="owner/reports" element={<ProtectedRoute roles={["company_owner"]}><OwnerReports /></ProtectedRoute>} />
          <Route path="owner/calls-emails" element={<ProtectedRoute roles={["company_owner"]}><OwnerCallsEmails /></ProtectedRoute>} />
          <Route path="owner/chat" element={<ProtectedRoute roles={["company_owner"]}><Chat /></ProtectedRoute>} />
          <Route path="chat" element={<ProtectedRoute roles={["sales_rep", "company_owner"]}><Chat /></ProtectedRoute>} />
          <Route path="work-report" element={<ProtectedRoute roles={["sales_rep", "company_owner"]}><WorkReport /></ProtectedRoute>} />
          <Route path="ai-leads" element={<ProtectedRoute roles={["company_owner", "super_admin"]}><AILeadFinder /></ProtectedRoute>} />
          <Route path="leads/agency" element={<ProtectedRoute roles={["super_admin"]}><AgencyWorkbench /></ProtectedRoute>} />
          <Route path="email-agent" element={<ProtectedRoute roles={["super_admin"]}><EmailAgent /></ProtectedRoute>} />
          <Route path="files" element={<Files />} />
          <Route path="settings" element={<Settings />} />
          <Route path="admin" element={<ProtectedRoute roles={["super_admin"]}><SuperAdmin /></ProtectedRoute>} />
          <Route path="admin/partners" element={<ProtectedRoute roles={["super_admin"]}><AdminPartners /></ProtectedRoute>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <Navigate to="/app/admin" replace />;
  return <Dashboard />;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppRoot />
        <Toaster position="top-right" />
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
